import React, { useCallback, useMemo } from 'react';
import { FieldValues, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { paperClasses, Stack, Typography } from '@mui/material';
import type { MuiTelInputCountry } from 'mui-tel-input';
import * as yup from 'yup';

import { CommonFormFooter } from '@/components/Form/Form';
import AdminInviteForm from '@/views/CreateOrganizationModal/AdminInviteForm';
import SettingsModalForm from '@/views/SettingsModalForm/SettingsModalForm';

interface InviteAdminModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { email: string; phone: string }) => void;
  prefilledEmail?: string;
  isLoading?: boolean;
  defaultCountry?: MuiTelInputCountry;
}

interface InviteAdminFormData {
  admin: {
    email: string;
    phone: string;
  };
}

const InviteAdminModalContent: React.FC<{
  onClose: () => void;
  isLoading?: boolean;
  defaultCountry?: MuiTelInputCountry;
}> = ({ onClose, isLoading, defaultCountry }) => {
  const { t } = useTranslation(['users', 'common']);
  const { formState } = useFormContext<InviteAdminFormData>();
  const { isValid } = formState;

  return (
    <>
      <Stack sx={{ p: 4, pt: 2, '& .MuiFormControl-root': { p: 0 } }} gap={2}>
        <Typography variant="body2" color="text.secondary">
          {t('users:users.inviteAdmin.description')}
        </Typography>
        <AdminInviteForm namePrefix="admin" defaultCountry={defaultCountry} />
      </Stack>
      <CommonFormFooter
        onReset={onClose}
        resetText={t('common:actions.cancel')}
        submitText={t('users:users.inviteAdmin.invite')}
        disableSubmit={!isValid || isLoading}
      />
    </>
  );
};

const InviteAdminModal: React.FC<InviteAdminModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  prefilledEmail = '',
  isLoading = false,
  defaultCountry
}) => {
  const { t } = useTranslation(['users', 'common', 'validation']);

  const defaultValues: InviteAdminFormData = useMemo(
    () => ({
      admin: {
        email: prefilledEmail,
        phone: ''
      }
    }),
    [prefilledEmail]
  );

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        admin: yup.object().shape({
          email: yup
            .string()
            .email(t('validation:email'))
            .required(t('validation:mandatory', { field: t('users:users.fields.email') })),
          phone: yup.string().required(t('validation:mandatory', { field: t('users:users.fields.phone') }))
        })
      }),
    [t]
  );

  const handleSave = useCallback(
    async (data: FieldValues) => {
      const formData = data as InviteAdminFormData;
      onSubmit(formData.admin);
    },
    [onSubmit]
  );

  return (
    <SettingsModalForm
      key={prefilledEmail}
      isOpen={isOpen}
      onClose={onClose}
      closeOnSave={false}
      onSave={handleSave}
      head={
        <Typography component="h2" variant="h3">
          {t('users:users.inviteAdmin.title')}
        </Typography>
      }
      sx={{
        [`& .${paperClasses.root}`]: {
          margin: 0,
          width: 500,
          height: 'unset'
        }
      }}
      defaultValues={defaultValues}
      validation={validationSchema}
    >
      <InviteAdminModalContent onClose={onClose} isLoading={isLoading} defaultCountry={defaultCountry} />
    </SettingsModalForm>
  );
};

export default InviteAdminModal;
